import * as React from 'react'
import * as ReactDOM from 'react-dom/client'

// Lazy load all route components to avoid early initialization issues
const LoginForm = React.lazy(() => import('./routes/login'))
const AdminLayout = React.lazy(() => import('./routes/layout').then(module => ({ default: module.AdminLayout })))
const Dashboard = React.lazy(() => import('./routes/dashboard').then(module => ({ default: module.Dashboard })))
const PlacesList = React.lazy(() => import('./routes/places/PlacesList').then(module => ({ default: module.PlacesList })))
const PlacesDetail = React.lazy(() => import('./routes/places/PlacesDetail').then(module => ({ default: module.PlacesDetail })))
const SubmissionsList = React.lazy(() => import('./routes/submissions/SubmissionsList').then(module => ({ default: module.SubmissionsList })))
const EditionsPage = React.lazy(() => import('./routes/editions/EditionsPage').then(module => ({ default: module.EditionsPage })))
const Translations = React.lazy(() => import('./routes/translations/Translations').then(module => ({ default: module.Translations })))
const UsersPage = React.lazy(() => import('./routes/users/UsersPage').then(module => ({ default: module.UsersPage })))
const FeedbackPage = React.lazy(() => import('./routes/feedback/FeedbackPage').then(module => ({ default: module.FeedbackPage })))

// Lazy loader wrappers to avoid static imports
const layoutLoader = async (...args) => {
  const { layoutLoader: loader } = await import('./routes/layout')
  return loader(...args)
}

const EditionsPageLoader = async (...args) => {
  const { EditionsPageLoader: loader } = await import('./routes/editions/EditionsPage')
  return loader(...args)
}

const translationsLoader = async (...args) => {
  const { translationsLoader: loader } = await import('./routes/translations/Translations')
  return loader(...args)
}

import {pb} from './utils/pb'

import {
  createBrowserRouter,
  RouterProvider,
  Navigate
} from "react-router-dom";

import { Provider } from './components/ui/provider'
// Mounted once for the whole app: without it every toaster.create() call
// in the routes is a silent no-op (nothing renders the toast region).
import { Toaster } from './components/ui/toaster'

// Add an error boundary component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("React Error Boundary caught an error:", error, errorInfo);
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 20, backgroundColor: '#ffeeee', color: 'red' }}>
          <h2>Something went wrong.</h2>
          <details style={{ whiteSpace: 'pre-wrap' }}>
            <summary>Show error details</summary>
            {this.state.error && this.state.error.toString()}
            <br />
            {this.state.errorInfo && this.state.errorInfo.componentStack}
          </details>
          <button onClick={() => window.location.reload()}>Reload page</button>
        </div>
      );
    }
    return this.props.children;
  }
}

const RequireAuth = ({ children, redirectTo = "/" }) => {
  let isAuthenticated = pb.authStore.isValid;
  return isAuthenticated ? children : <Navigate to={redirectTo} />;
}

const router = createBrowserRouter([
  {
    path: "/login",
    element: <LoginForm/>
  },
  {
    path: "/",
    loader: pb.authStore.isValid ? layoutLoader : null,
    element: 
      <RequireAuth redirectTo="/login">
        <AdminLayout/>
      </RequireAuth>,
    children: [
      {
        path: "dashboard",
        element: <RequireAuth redirectTo="/login"><Dashboard/></RequireAuth>      },
      {
        path: "places",
        element: <RequireAuth redirectTo="/login"><PlacesList/></RequireAuth>
      },
      {
        path: "places/:id/edit",
        element: <RequireAuth redirectTo="/login"><PlacesDetail/></RequireAuth>
      },
      {
        path: "submissions",
        element: <RequireAuth redirectTo="/login"><SubmissionsList/></RequireAuth>
      },
      {
        path: "translations",
        element: <RequireAuth redirectTo="/login"><Translations/></RequireAuth>,
        loader: translationsLoader
      },
      {
        path: "editions",
        element: <RequireAuth redirectTo="/login"><EditionsPage/></RequireAuth>,
        loader: EditionsPageLoader
      },
      {
        path: "users",
        element: <RequireAuth redirectTo="/login"><UsersPage/></RequireAuth>
      },
      {
        path: "feedback",
        element: <RequireAuth redirectTo="/login"><FeedbackPage/></RequireAuth>
      },
      {
        path: "*",
        element: <Navigate to="dashboard" />
      }
    ]
  }
]);

const rootElement = document.getElementById('root')

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <ErrorBoundary>
      <Provider>
        <React.Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>Loading...</div>}>
          <RouterProvider router={router} />
        </React.Suspense>
        <Toaster />
      </Provider>
    </ErrorBoundary>
  </React.StrictMode>,
)
